// S-03 / S-04 Edit time zones (Frontend §7.2; D-38): IANA names from `Intl.supportedValuesOf('timeZone')`, whole-hour
// UTC offsets only (all US zones are), US zones first. The server checks the same rule.
import type { SelectOption } from '@pa/ui'

/** The US zones offered first, with friendly names. Only those the browser supports are shown. */
export const US_ZONES: readonly { zone: string; label: string }[] = [
  { zone: 'America/New_York', label: 'Eastern (New York)' },
  { zone: 'America/Chicago', label: 'Central (Chicago)' },
  { zone: 'America/Denver', label: 'Mountain (Denver)' },
  { zone: 'America/Phoenix', label: 'Mountain, no DST (Phoenix)' },
  { zone: 'America/Los_Angeles', label: 'Pacific (Los Angeles)' },
  { zone: 'America/Anchorage', label: 'Alaska (Anchorage)' },
  { zone: 'Pacific/Honolulu', label: 'Hawaii (Honolulu)' },
  { zone: 'America/Detroit', label: 'Eastern (Detroit)' },
  { zone: 'America/Boise', label: 'Mountain (Boise)' },
  { zone: 'America/Puerto_Rico', label: 'Atlantic (Puerto Rico)' },
]

/** The zone's UTC offset in minutes at `at` (for example −300 for New York in January). */
export function offsetMinutes(zone: string, at: Date): number {
  const part = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'longOffset' })
    .formatToParts(at)
    .find((p) => p.type === 'timeZoneName')?.value
  const m = /GMT([+-])(\d{2}):(\d{2})/.exec(part ?? '')
  if (!m) return 0
  const sign = m[1] === '-' ? -1 : 1
  return sign * (Number(m[2]) * 60 + Number(m[3]))
}

/** D-38: whole-hour offsets on 1 January and 1 July of `year` (the backend's check). */
export function isWholeHourZone(zone: string, year: number): boolean {
  return [new Date(Date.UTC(year, 0, 1)), new Date(Date.UTC(year, 6, 1))].every(
    (d) => offsetMinutes(zone, d) % 60 === 0,
  )
}

const supported = (): string[] =>
  typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : US_ZONES.map((z) => z.zone)

const fmtOffset = (minutes: number) => {
  const sign = minutes < 0 ? '−' : '+'
  return `UTC${sign}${String(Math.abs(minutes) / 60)}`
}

/** The options for the time zone select: "United States" first, then every other whole-hour zone A–Z. */
export function timeZoneOptions(now: Date = new Date(), zones: readonly string[] = supported()): SelectOption[] {
  const year = now.getUTCFullYear()
  const available = new Set(zones.filter((z) => isWholeHourZone(z, year)))
  const us = US_ZONES.filter((z) => available.has(z.zone)).map((z) => ({
    value: z.zone,
    label: z.label,
    sub: `${z.zone} · ${fmtOffset(offsetMinutes(z.zone, new Date(Date.UTC(year, 0, 1))))}`,
    group: 'United States',
  }))
  const usSet = new Set(us.map((o) => o.value))
  const other = [...available]
    .filter((z) => !usSet.has(z))
    .sort()
    .map((z) => ({
      value: z,
      label: z.replace(/_/g, ' '),
      sub: fmtOffset(offsetMinutes(z, new Date(Date.UTC(year, 0, 1)))),
      group: 'Other time zones',
    }))
  return [...us, ...other]
}

/** A short label for a stored zone. */
export const timeZoneLabel = (zone: string): string =>
  US_ZONES.find((z) => z.zone === zone)?.label ?? zone.replace(/_/g, ' ')
